/**
 * Admin Certifications Management Hook
 * 
 * Provides administrative functions for reviewing and managing vendor certifications.
 * Allows admins to approve or reject vendor-submitted certifications, track verification
 * status, and ensure compliance with industry standards.
 * 
 * **Certification Review Workflow:**
 * 1. Vendor uploads certification documents and details
 * 2. Certification appears in admin review queue (status: pending)
 * 3. Admin reviews document, expiry date, issuing organization
 * 4. Admin approves (status: verified) or rejects (status: rejected) with notes
 * 5. Vendor is notified of review decision
 * 6. Verified certifications display on vendor profile
 * 
 * **Key Features:**
 * - Real-time certification updates via Supabase subscriptions
 * - Status filtering (pending, verified, rejected, all)
 * - Document URL generation with signed URLs
 * - Automatic vendor notifications on review
 * - Comprehensive certification details
 * 
 * **Certification Verification:**
 * - Admins review certification documents
 * - Check expiry dates for time-sensitive certs
 * - Verify issuing organization authenticity
 * - Add review notes for audit trail
 * - Track reviewer identity and timestamp
 * 
 * **Status Values:**
 * - pending: Awaiting admin review
 * - verified: Approved by admin
 * - rejected: Rejected with reason notes
 * - expired: Certification past expiry date
 * 
 * **Use Cases:**
 * - Admin certification review dashboard
 * - Vendor compliance management
 * - Industry standard verification
 * - Audit trail for certifications
 * - Quality control for vendor network
 * 
 * @returns {Object} Admin certification management functions
 * @returns {AdminCertificationView[]} certifications - Array of certifications
 * @returns {boolean} loading - Loading state
 * @returns {string} statusFilter - Current filter (pending/verified/rejected/all)
 * @returns {Function} setStatusFilter - Update status filter
 * @returns {Function} approveCertification - Approve a certification
 * @returns {Function} rejectCertification - Reject a certification with notes
 * @returns {Function} getDocumentUrl - Get signed URL for certification document
 * @returns {Function} refetch - Manually refresh certifications
 * 
 * @example
 * // Admin certification review
 * const { 
 *   certifications, 
 *   loading, 
 *   approveCertification, 
 *   rejectCertification,
 *   getDocumentUrl 
 * } = useAdminCertifications();
 * 
 * const handleApprove = async (certId: string) => {
 *   await approveCertification(certId, 'Verified with ISO registry');
 *   toast({ title: 'Certification approved' });
 * };
 * 
 * const handleReject = async (certId: string) => {
 *   await rejectCertification(
 *     certId, 
 *     'Certificate expired. Please resubmit current certificate.'
 *   );
 * };
 * 
 * @example
 * // Filter certifications by status
 * const { certifications, setStatusFilter } = useAdminCertifications();
 * 
 * <Tabs onValueChange={setStatusFilter}>
 *   <TabsList>
 *     <TabsTrigger value="pending">Pending</TabsTrigger>
 *     <TabsTrigger value="verified">Verified</TabsTrigger>
 *     <TabsTrigger value="rejected">Rejected</TabsTrigger>
 *   </TabsList>
 * </Tabs>
 * 
 * @example
 * // View certification document
 * const { getDocumentUrl } = useAdminCertifications();
 * 
 * const viewDocument = async (cert: AdminCertificationView) => {
 *   if (cert.document_url) {
 *     const signedUrl = await getDocumentUrl(cert.document_url);
 *     if (signedUrl) {
 *       window.open(signedUrl, '_blank');
 *     }
 *   }
 * };
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

export interface AdminCertificationView {
  id: string;
  vendor_profile_id: string;
  vendor_name: string;
  vendor_user_id: string;
  certification_type_id?: string;
  certification_name?: string;
  certification_type_name?: string;
  category_name?: string;
  issuing_organization: string;
  certificate_number?: string;
  issue_date?: string;
  expiry_date?: string;
  document_url?: string;
  verification_status: string;
  reviewed_by_admin_id?: string;
  review_notes?: string;
  reviewed_at?: string;
  created_at: string;
}

export const useAdminCertifications = () => {
  const [certifications, setCertifications] = useState<AdminCertificationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const { toast } = useToast();

  const fetchCertifications = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('vendor_certifications')
        .select(`
          id,
          vendor_profile_id,
          certification_type_id,
          certification_name,
          issuing_organization,
          certificate_number,
          issue_date,
          expiry_date,
          document_url,
          verification_status,
          reviewed_by_admin_id,
          review_notes,
          reviewed_at,
          created_at,
          certification_type:certification_types!vendor_certifications_certification_type_id_fkey(
            name,
            category:certification_categories!certification_types_category_id_fkey(name)
          )
        `)
        .order('created_at', { ascending: false });

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('verification_status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Build a map of vendor_profile_id -> { user_id, business_name }
      const profileIds: string[] = Array.from(
        new Set((data || []).map((cert: any) => cert.vendor_profile_id).filter(Boolean))
      );

      let vendorByProfileId: Record<string, { user_id: string; business_name: string }> = {};
      if (profileIds.length > 0) {
        const { data: vendors } = await supabase
          .from('vendor_profiles_enhanced')
          .select('id, user_id, business_name')
          .in('id', profileIds);

        if (vendors) {
          vendorByProfileId = vendors.reduce((acc: Record<string, { user_id: string; business_name: string }>, v: any) => {
            if (v.id) acc[v.id] = { user_id: v.user_id, business_name: v.business_name };
            return acc;
          }, {});
        }
      }

      // Transform data to match interface
      const transformed = (data || []).map((cert: any) => {
        const profileInfo = vendorByProfileId[cert.vendor_profile_id] || { user_id: '', business_name: 'Unknown' };
        return {
          id: cert.id,
          vendor_profile_id: cert.vendor_profile_id,
          vendor_name: profileInfo.business_name,
          vendor_user_id: profileInfo.user_id,
          certification_type_id: cert.certification_type_id,
          certification_name: cert.certification_name,
          certification_type_name: cert.certification_type?.name,
          category_name: cert.certification_type?.category?.name,
          issuing_organization: cert.issuing_organization,
          certificate_number: cert.certificate_number,
          issue_date: cert.issue_date,
          expiry_date: cert.expiry_date,
          document_url: cert.document_url,
          verification_status: cert.verification_status,
          reviewed_by_admin_id: cert.reviewed_by_admin_id,
          review_notes: cert.review_notes,
          reviewed_at: cert.reviewed_at,
          created_at: cert.created_at,
        } as AdminCertificationView;
      });

      setCertifications(transformed);
    } catch (error: any) {
      console.error('Error fetching certifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load certifications',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const approveCertification = async (certId: string, notes?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('vendor_certifications')
        .update({
          verification_status: 'verified',
          reviewed_by_admin_id: user.id,
          review_notes: notes || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', certId);

      if (error) throw error;

      // Call notification edge function
      const cert = certifications.find(c => c.id === certId);
      if (cert) {
        try {
          const { error: notifError } = await invokeEdgeFunction('certification-review-notify', {
            body: {
              vendorUserId: cert.vendor_user_id,
              certificationName: cert.certification_type_name || cert.certification_name || 'Certification',
              status: 'approved',
              notes,
            },
            callerOperation: 'useAdminCertifications.approveCertification',
          });

          if (notifError) {
            console.error('Notification failed:', notifError);
            toast({
              title: 'Warning',
              description: 'Certification approved but notification may not have been sent',
              variant: 'default',
            });
          }
        } catch (notifError) {
          console.error('Failed to send notification:', notifError);
          toast({
            title: 'Warning',
            description: 'Certification approved but notification failed',
            variant: 'default',
          });
        }
      }

      toast({
        title: 'Success',
        description: 'Certification approved',
      });

      await fetchCertifications();
    } catch (error: any) {
      console.error('Error approving certification:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve certification',
        variant: 'destructive',
      });
    }
  };

  const rejectCertification = async (certId: string, notes: string) => {
    if (!notes.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a reason for rejection',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('vendor_certifications')
        .update({
          verification_status: 'rejected',
          reviewed_by_admin_id: user.id,
          review_notes: notes,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', certId);

      if (error) throw error;

      // Call notification edge function
      const cert = certifications.find(c => c.id === certId);
      if (cert) {
        try {
          const { error: notifError } = await invokeEdgeFunction('certification-review-notify', {
            body: {
              vendorUserId: cert.vendor_user_id,
              certificationName: cert.certification_type_name || cert.certification_name || 'Certification',
              status: 'rejected',
              notes,
            },
            callerOperation: 'useAdminCertifications.rejectCertification',
          });

          if (notifError) {
            console.error('Notification failed:', notifError);
            toast({
              title: 'Warning',
              description: 'Certification rejected but notification may not have been sent',
              variant: 'default',
            });
          }
        } catch (notifError) {
          console.error('Failed to send notification:', notifError);
          toast({
            title: 'Warning',
            description: 'Certification rejected but notification failed',
            variant: 'default',
          });
        }
      }

      toast({
        title: 'Success',
        description: 'Certification rejected',
      });

      await fetchCertifications();
    } catch (error: any) {
      console.error('Error rejecting certification:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject certification',
        variant: 'destructive',
      });
    }
  };

  const getDocumentUrl = async (storagePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, 3600);

      if (error) throw error;
      return data.signedUrl;
    } catch (error: any) {
      console.error('Error getting document URL:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchCertifications();

    // Subscribe to changes
    const channel = supabase
      .channel('admin-certifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vendor_certifications',
        },
        () => {
          fetchCertifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [statusFilter]);

  return {
    certifications,
    loading,
    statusFilter,
    setStatusFilter,
    approveCertification,
    rejectCertification,
    getDocumentUrl,
    refetch: fetchCertifications,
  };
};
